import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Camera,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  RefreshCw,
  Search,
  X,
  ZoomIn,
} from "lucide-react";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { ATTENDANCE_IMAGE_COMPANY_ID } from "../../config/attendanceImageStorage";
import {
  listBranchDayImages,
  listEmployeeDayImages,
  loadAttendanceBranchEmployees,
  loadEmployeeNameMap,
  resolveBranchStorageId,
} from "../../services/attendanceImageService";

function ymdToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shiftYmd(ymd, deltaDays) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymdToday();
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + deltaDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function PayrollAttendanceImagesPage() {
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [selectedShopLabel, setSelectedShopLabel] = useState(branchPool[0]?.label || "Main Shop");
  const [selectedDate, setSelectedDate] = useState(ymdToday);
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadNote, setLoadNote] = useState("");
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const currentShop = useMemo(
    () => branchPool.find((b) => b.label === selectedShopLabel) || branchPool[0],
    [branchPool, selectedShopLabel]
  );

  const loadEmployees = useCallback(async () => {
    if (!currentShop) return;
    try {
      const { employees: list } = await loadAttendanceBranchEmployees(currentShop);
      setEmployees(list);
    } catch (e) {
      console.error(e);
      setEmployees([]);
    }
  }, [currentShop]);

  const loadImages = useCallback(async () => {
    if (!currentShop) return;
    setLoading(true);
    setError("");
    setLoadNote("Loading employees…");
    try {
      const [{ employees: emps, nameById: branchNames }, allNames] = await Promise.all([
        loadAttendanceBranchEmployees(currentShop),
        loadEmployeeNameMap(),
      ]);
      setEmployees(emps);
      const branchId = resolveBranchStorageId(currentShop, emps);
      const companyId = emps[0]?.companyId || ATTENDANCE_IMAGE_COMPANY_ID;

      setLoadNote("Loading punch photos from Storage…");
      let rows;
      if (employeeFilter && employeeFilter !== "all") {
        rows = await listEmployeeDayImages({
          companyId,
          branchId,
          ymd: selectedDate,
          employeeId: employeeFilter,
        });
      } else {
        rows = await listBranchDayImages({
          companyId,
          branchId,
          ymd: selectedDate,
          onProgress: (n) => setLoadNote(`Loaded ${n} photo(s)…`),
        });
      }

      const nameMap = new Map([...allNames, ...(branchNames || [])]);
      if (!nameMap.size) {
        for (const e of emps) nameMap.set(e.employeeId, e.name);
      }
      const enriched = rows.map((r) => ({
        ...r,
        employeeName: nameMap.get(r.employeeId) || nameMap.get(String(r.employeeId)) || r.employeeId || "—",
      }));

      setImages(enriched);
      setLoadNote("");
      if (!enriched.length) {
        setError(
          `No punch photos found for ${selectedDate} (company ${companyId}, branch ${branchId}).`
        );
      }
    } catch (e) {
      console.error(e);
      setImages([]);
      setError(e?.message || "Failed to load attendance images. Check Storage rules for attendance-images/.");
      setLoadNote("");
    } finally {
      setLoading(false);
    }
  }, [currentShop, selectedDate, employeeFilter]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return images;
    return images.filter(
      (r) =>
        r.employeeName.toLowerCase().includes(q) ||
        String(r.employeeId).toLowerCase().includes(q) ||
        String(r.punchTime || "").includes(q)
    );
  }, [images, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const img of filtered) {
      const key = img.employeeId || img.employeeName;
      if (!map.has(key)) {
        map.set(key, { employeeId: img.employeeId, employeeName: img.employeeName, photos: [] });
      }
      map.get(key).photos.push(img);
    }
    return [...map.values()].sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <MenuBar />
      <HeaderBar />

      <main className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll</p>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <Camera className="h-7 w-7 text-indigo-600" aria-hidden />
              Punch photos
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Selfie images from mobile punching. Names from Firestore{" "}
              <code className="rounded bg-slate-200 px-1 text-xs">employees/&#123;id&#125;/name</code>
              {" · "}Storage{" "}
              <code className="rounded bg-slate-200 px-1 text-xs">
                attendance-images/company/default/branch/1/YYYY/MM/DD/employeeId/HHMMSS.jpg
              </code>
            </p>
          </div>
          <button
            type="button"
            onClick={loadImages}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-wrap gap-1">
              {branchPool.map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={() => setSelectedShopLabel(b.label)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    selectedShopLabel === b.label
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
              <button
                type="button"
                onClick={() => setSelectedDate((d) => shiftYmd(d, -1))}
                className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50"
                aria-label="Previous day"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={() => setSelectedDate((d) => shiftYmd(d, 1))}
                className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50"
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setSelectedDate(ymdToday())}
                className="rounded-lg bg-slate-100 px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
              >
                Today
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <div className="relative min-w-[12rem] flex-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee or time…"
                className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm"
              />
            </div>
            <select
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="min-w-[14rem] rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="all">All employees</option>
              {employees.map((e) => (
                <option key={e.employeeId} value={e.employeeId}>
                  {e.name} ({e.employeeId})
                </option>
              ))}
            </select>
          </div>
        </div>

        {loadNote && (
          <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            {loadNote}
          </div>
        )}

        {error && !loading && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <ImageOff className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && !grouped.length ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20">
            <div className="mb-3 h-10 w-10 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
            <p className="text-sm text-slate-600">Loading punch photos…</p>
          </div>
        ) : null}

        {!loading && grouped.length === 0 && !error ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500">
            <Camera className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p>No photos match your filters.</p>
          </div>
        ) : null}

        <div className="space-y-8">
          {grouped.map((group) => (
            <section key={group.employeeId || group.employeeName}>
              <h2 className="mb-3 text-sm font-bold text-slate-800">
                {group.employeeName}
                {group.employeeId ? (
                  <span className="ml-2 font-normal text-slate-500">({group.employeeId})</span>
                ) : null}
                <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                  {group.photos.length} photo{group.photos.length === 1 ? "" : "s"}
                </span>
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {group.photos.map((img) => (
                  <button
                    key={img.storagePath}
                    type="button"
                    onClick={() => setLightbox(img)}
                    className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <img
                      src={img.url}
                      alt={`${group.employeeName} punch`}
                      className="aspect-square w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-2 text-left">
                      <p className="text-xs font-semibold text-white">
                        {img.punchTime || img.fileName}
                      </p>
                    </div>
                    <div className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 opacity-0 transition group-hover:opacity-100">
                      <ZoomIn className="h-4 w-4 text-white" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      {lightbox ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          role="presentation"
        >
          <div
            className="relative max-h-[90vh] max-w-4xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute -top-10 right-0 rounded-full p-2 text-white hover:bg-white/10"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={lightbox.url}
              alt="Punch"
              className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl"
            />
            <div className="mt-3 text-center text-sm text-white">
              <p className="font-semibold">{lightbox.employeeName || lightbox.employeeId}</p>
              <p className="text-white/80">
                {selectedDate}
                {lightbox.punchTime ? ` · ${lightbox.punchTime}` : ""} · {lightbox.fileName}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
